import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IGlobalParam } from '@interfaces/global-param';
import { UserSettings } from '@interfaces/user-settings';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { PermissionsService } from '@services/auth/permissions.service';
import { TimeoutService } from '@services/auth/timeout.service';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

export const MAX_PERIOD = 50;

@Injectable({ providedIn: 'root' })
export class GlobalParamsService {
  private apiUrl;
  private updateApiUrl;
  private globalSettingsUrl: string;
  /**
   * Determines if the user can run simulations even if the Required Import flag is set to YES
   */
  public ignoreImportWarning = false;
  private _isAzureImport = true;
  public get isAzureImport(): boolean {
    return this._isAzureImport;
  }
  private _numPeriods;

  public get numPeriods() {
    return this._numPeriods;
  }

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigProvider,
    private timeout: TimeoutService,
    private permissions: PermissionsService
  ) {
    this.appConfig.environment.subscribe(env => {
      this.apiUrl = `${env.API.CALCAPI}/api/param/GetSettings`;
      this.updateApiUrl = `${env.API.CALCAPI}/api/param/UpdateNumPeriods`;
      this.globalSettingsUrl = `${env.API.USERAPI}/api/settings`;
    });
  }

  /**
   * Obtains the GlobalParams from the database as a collection of IGlobalParams
   * @augments GlobalSettingsComponent
   * @emits ignoreImportWarning flag to the system, obtained from the GlobalParams. Defaults to false.
   */
  public getParams(): Observable<IGlobalParam[]> {
    return this.http.get<IGlobalParam[]>(this.apiUrl).pipe(
      shareReplay(1),
      map((params: IGlobalParam[]) => {
        const filteredParams = params.filter(param => {
          param.Valid = true;
          param.Type = 'number'; // default param type
          param.ReadOnly = false;

          if (param.ParamName === '0' && param.ParamGroup === 'PERIOD') {
            param.ParamName = 'CP Date';
            param.Type = 'text';
            param.ReadOnly = true;
          }

          if (param.ParamGroup === 'USER' && param.ParamName === 'NUM_PERIODS') {
            param.ValidationMessage = `Enter a number between 1 and ${MAX_PERIOD}`;
            this._numPeriods = param.ParamValue;
          }

          if (param.ParamGroup === 'USER' && param.ParamName === 'SURVIVAL_HORIZON') {
            param.ValidationMessage = `Enter a number greater than 0`;
          }

          if (param.ParamGroup === 'USER' && param.ParamName === 'MAX_IDLE_TIME') {
            this.timeout.setMaxIdleTime(+param.ParamValue);

            // This param is only shown to System Admins
            if (this.permissions.getPermission('GlobalSettings') !== 'W') {
              param.ReadOnly = true;
              //return false;
            }
            param.ValidationMessage = `Idle time must be at least 5 mins.`;
          }
          if (param.ParamGroup === 'LOCALSTORAGE' && param.ParamName === 'IMPORT_FILE_LOCATION') {
            param.ValidationMessage = `You need to enter a file path`;
            param.Type = 'text';
          }
          return true;
        });

        filteredParams
          .sort((a, b) => (a.ParamName > b.ParamName ? 1 : -1))
          .sort((a, b) => (a.ParamGroup > b.ParamGroup ? 1 : -1))
          .sort((a, b) => (a.ReadOnly > b.ReadOnly ? -1 : 1));

        return filteredParams;
      }),
      // Placeholder before we implement the override flow within the application
      tap(params => {
        const ignoreImportWarningFlag = params.find(param => param.ParamName === 'IGNORE_REQUIRED_IMPORT' && param.ParamGroup === 'USER');
        if (ignoreImportWarningFlag) {
          this.ignoreImportWarning = Boolean(ignoreImportWarningFlag.ParamValue);
        }

        const importFileLocationFlagFound = params.find(
          param => param.ParamGroup === 'LOCALSTORAGE' && param.ParamName === 'IMPORT_FILE_LOCATION'
        );
        if (importFileLocationFlagFound) this._isAzureImport = false;
      })
    );
  }

  public updateUserSettings(periods: number, horizon: string, file_location?: string, maxIdleTime?: number) {
    const settings = new UserSettings();
    settings.Survival_Horizon = horizon;
    settings.Num_Periods = periods;
    settings.File_Location = !file_location ? null : file_location;
    // If the max_idle_time is not changed, pass the default value from the api call
    settings.Max_Idle_Time = maxIdleTime ? maxIdleTime : this.timeout.maxIdleTime;
    return this.http.post<boolean>(this.updateApiUrl, settings);
  }

  // public getGlobalSettings() {
  //   return this.http.get<IGlobalSetting[]>(this.globalSettingsUrl);
  // }
}
